import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Download, Loader2, ShieldCheck, BarChart3, Check, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * Platform-admin tools panel. Rendered on the Profile page for users with
 * ``lifetime_free`` set — the same marker used by the backend for
 * /admin/brand-kit.zip and the trial-email endpoints. Not visible to
 * regular household owners.
 */
export default function AdminTools() {
  const { user, billing } = useAuth();
  const [downloading, setDownloading] = useState(false);

  const isAdmin = Boolean(
    user?.lifetime_free || billing?.lifetime_free || billing?.status === "lifetime_free"
  );
  if (!isAdmin) return null;

  const downloadBrandKit = async () => {
    setDownloading(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${base}/api/admin/brand-kit.zip`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        if (res.status === 403) {
          toast.error("Only platform admins can download the brand kit.");
        } else {
          toast.error(`Couldn't download the brand kit (HTTP ${res.status}).`);
        }
        return;
      }
      const blob = await res.blob();
      const filename =
        res.headers.get("content-disposition")?.match(/filename="?([^"]+)"?/)?.[1] ||
        `hearthhouse-brand-kit-${new Date().toISOString().slice(0, 10)}.zip`;

      // Trigger the browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("Brand kit downloaded.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section
      data-testid="admin-tools-panel"
      className="rounded-3xl border border-hearth-line/70 bg-gradient-to-br from-hearth-cream/80 via-white to-hearth-stone/60 p-8 shadow-sm"
    >
      <div className="flex items-center gap-2 caption text-hearth-clayDark tracking-widest">
        <ShieldCheck size={14} />
        <span>Platform admin</span>
      </div>
      <h2 className="font-display text-3xl font-semibold tracking-tight mt-2 text-hearth-ink">
        Admin toolkit
      </h2>
      <p className="text-sm text-hearth-mute mt-2 max-w-xl">
        Utilities visible only to platform admins. These aren't part of the
        household experience — use them for supporting users, press
        outreach, and design work.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Brand kit card */}
        <div className="rounded-2xl bg-white border border-hearth-line p-5 flex flex-col">
          <div className="caption text-hearth-mute tracking-widest">Brand kit</div>
          <h3 className="font-display text-xl font-semibold mt-1">HearthHouse brand assets</h3>
          <p className="text-sm text-hearth-mute mt-2 flex-1">
            SVG + PNG logos, colour palette CSS + swatch card, and a typography
            reference — everything designers or partners need. Generated on the
            fly, always current.
          </p>
          <Button
            data-testid="download-brand-kit-btn"
            onClick={downloadBrandKit}
            disabled={downloading}
            className="mt-4 h-11 rounded-full bg-hearth-ink hover:bg-hearth-ink/90 text-white font-medium"
          >
            {downloading ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} /> Packaging…
              </>
            ) : (
              <>
                <Download size={16} className="mr-2" /> Download Brand Kit
              </>
            )}
          </Button>
        </div>

        {/* Analytics hygiene */}
        <div className="rounded-2xl bg-white border border-hearth-line p-5 flex flex-col">
          <div className="caption text-hearth-mute tracking-widest flex items-center gap-1.5">
            <BarChart3 size={12} /> Analytics
          </div>
          <h3 className="font-display text-xl font-semibold mt-1">Keep testing out of the numbers</h3>
          <p className="text-sm text-hearth-mute mt-2 flex-1">
            Bots + admin/lifetime-free accounts are auto-filtered. To
            exclude a specific browser (your phone, a friend's laptop),
            open the app once with the opt-out link — it sticks per device
            via localStorage.
          </p>
          <div className="mt-3 space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="chip bg-hearth-sage/15 text-hearth-sageDark inline-flex items-center gap-1">
                <Check size={10} /> auto
              </span>
              <span className="text-hearth-mute">Your admin login opts you out already.</span>
            </div>
            <OptOutRow />
          </div>
          <p className="text-[11px] text-hearth-mute mt-4 leading-relaxed">
            <strong className="text-hearth-ink">One-time PostHog dashboard steps</strong> (recommended for airtight numbers):
            <br />1. Project Settings → <em>Data Management → Discard events</em> → tick <em>Discard client IPs and user agents that look like bots</em>.
            <br />2. Project Settings → <em>Autocapture</em> → paste your home / office IPs into <em>Ignored IPs</em>. Grab your current IP from{" "}
            <a href="https://api.ipify.org" target="_blank" rel="noreferrer" className="underline text-hearth-clayDark">api.ipify.org</a>.
          </p>
        </div>

        {/* Placeholder for future admin utilities so the panel doesn't feel
            single-purpose. Purely informational at the moment. */}
        <div className="rounded-2xl border border-dashed border-hearth-line p-5 flex flex-col items-start justify-center bg-white/30">
          <div className="caption text-hearth-mute tracking-widest">More coming</div>
          <p className="text-sm text-hearth-mute mt-2">
            Subscription roster, founding members panel, and trial-email
            controls will live here next. Ping the team if there's a tool
            you want first.
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * Little inline widget with a copy-to-clipboard button for the opt-out
 * URL. Bookmarking this link on any browser you QA with keeps that
 * device's traffic out of PostHog forever.
 */
function OptOutRow() {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/?ph_opt_out=1`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Copied — open it once on any device you test from.");
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast.error("Couldn't copy — long-press the link instead.");
    }
  };
  return (
    <div className="flex items-center gap-2 min-w-0">
      <code
        data-testid="analytics-optout-url"
        className="font-mono text-[10px] px-2 py-1 rounded-md bg-hearth-stone text-hearth-ink truncate flex-1"
      >
        {url}
      </code>
      <button
        data-testid="analytics-optout-copy"
        onClick={copy}
        title="Copy opt-out URL"
        className="p-1.5 rounded-full text-hearth-mute hover:text-hearth-clayDark hover:bg-hearth-clay/10 transition shrink-0"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}
